import './scss/main.scss'

import { createApp, nextTick } from 'vue'
import App from './App.vue'
import i18n from './i18n'
import { createVuestic } from 'vuestic-ui'
import { createGtm } from '@gtm-support/vue-gtm'

import stores from './stores'
import router from './router'
import vuesticGlobalConfig from './services/vuestic-ui/global-config'
import { useAuthStore } from './stores/auth-store'
import { useProjectsStore } from './stores/projects-store'

const app = createApp(App)

app.use(stores)
app.use(router)
app.use(i18n)
app.use(createVuestic({ config: vuesticGlobalConfig }))

// Navigation guard to ensure authentication and projects exist
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Check if route requires authentication (all routes except login/signup/change-password)
  const isAuthRoute = to.name === 'login' || to.name === 'signup'
  const isChangePasswordRoute = to.name === 'change-password'
  const requiresAuth = !isAuthRoute && !isChangePasswordRoute && to.path !== '/auth' && to.path.startsWith('/')

  // Change password route requires authentication
  if (isChangePasswordRoute && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }

  // If route requires auth but user is not authenticated, redirect to login
  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }

  // If user is already on auth routes (login/signup) and is authenticated, redirect to projects
  // But allow change-password for authenticated users
  if (isAuthRoute && authStore.isAuthenticated) {
    next({ name: 'projects' })
    return
  }

  // Check if route requires admin role
  if (to.name === 'admin-users') {
    const isAdmin = authStore.user?.role?.toLowerCase() === 'admin'
    if (!isAdmin) {
      // Redirect non-admin users
      next({ name: 'projects' })
      return
    }
  }

  // Only check for projects if user is authenticated and accessing admin routes
  if (authStore.isAuthenticated && requiresAuth) {
    // Skip check if already on projects page
    if (to.name === 'projects') {
      next()
      return
    }

    try {
      const projectsStore = useProjectsStore()

      // Only fetch if we haven't loaded projects yet
      if (projectsStore.items.length === 0) {
        await projectsStore.getAll()
      }

      // If no projects exist, redirect to projects page
      if (!projectsStore.hasProjects) {
        next({ name: 'projects' })
        return
      }
    } catch {
      // If there's an error fetching projects, allow navigation to continue
      // The projects page will handle the error
    }
  }

  next()
})

if (import.meta.env.VITE_APP_GTM_ENABLED) {
  app.use(
    createGtm({
      id: import.meta.env.VITE_APP_GTM_KEY,
      debug: false,
      vueRouter: router,
    }),
  )
}

app.mount('#app')

// Initialize auth store from localStorage after app is mounted
// Use nextTick to ensure stores are fully initialized
nextTick(async () => {
  const authStore = useAuthStore()
  authStore.loadAuthFromStorage()
  
      // Fetch user details if authenticated
      if (authStore.isAuthenticated) {
        try {
          await authStore.fetchUserDetails()
        } catch (error) {
          // If token is invalid or forbidden, clear auth and redirect to login
          if (error instanceof Error && (error.message === 'Unauthorized' || error.message === 'Forbidden')) {
            authStore.clearAuth()
            // Redirect to login if not already on auth route
            const currentRoute = router.currentRoute.value
            if (currentRoute.name !== 'login' && currentRoute.name !== 'signup') {
              router.push({ name: 'login' })
            }
          }
        }
      }
})
